import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { createInvite, checkDuplicateInvite, checkExistingMember } from "@/lib/api/invites";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Shield, Send, Loader2, AlertTriangle, XCircle, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

interface InviteFormProps {
  workspaceId: string;
  userId: string;
  onInviteCreated: () => void;
  usedSeats?: number;
  maxSeats?: number;
}

export function InviteForm({ workspaceId, userId, onInviteCreated, usedSeats, maxSeats }: InviteFormProps) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [loading, setLoading] = useState(false);

  // Calculate seat status
  const hasSeatInfo = usedSeats !== undefined && maxSeats !== undefined;
  const remainingSeats = hasSeatInfo ? maxSeats - usedSeats : null;
  const isAtLimit = hasSeatInfo && remainingSeats !== null && remainingSeats <= 0;
  const isNearLimit = hasSeatInfo && remainingSeats !== null && remainingSeats > 0 && remainingSeats <= Math.ceil(maxSeats * 0.2);
  const isLastSeat = hasSeatInfo && remainingSeats === 1;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
      return;
    }

    if (isAtLimit) {
      toast.error("Seat-Limit erreicht. Bitte upgraden Sie Ihren Plan oder entfernen Sie inaktive Mitglieder.");
      return;
    }

    setLoading(true);
    try {
      const normalizedEmail = email.toLowerCase().trim();

      // Prüfe auf bereits existierende Mitgliedschaft
      const isExistingMember = await checkExistingMember(workspaceId, normalizedEmail);
      if (isExistingMember) {
        toast.error("Diese Person ist bereits Mitglied des Workspace.");
        setLoading(false);
        return;
      }

      // Prüfe auf doppelte Einladung
      const isDuplicate = await checkDuplicateInvite(workspaceId, normalizedEmail);
      if (isDuplicate) {
        toast.error("Es existiert bereits eine aktive Einladung für diese E-Mail-Adresse.");
        setLoading(false);
        return;
      }

      const invite = await createInvite({
        workspaceId,
        email: normalizedEmail,
        role,
        createdBy: userId,
      });

      const { error: emailError } = await supabase.functions.invoke('send-team-invite', {
        body: { inviteId: invite.id },
      });

      if (emailError) {
        console.error('Fehler beim E-Mail-Versand:', emailError);
        toast.error("Einladung erstellt, aber E-Mail konnte nicht gesendet werden.");
      } else {
        toast.success(`Einladung an ${email} wurde versendet.`);
      }

      setEmail("");
      setRole('member');
      onInviteCreated();
    } catch (error: any) {
      // Check for seat limit error from backend
      if (error.message?.includes('Seat limit') || error.message?.includes('Seat-Limit')) {
        toast.error("Seat-Limit erreicht. Bitte upgraden Sie Ihren Plan.");
      } else {
        toast.error(error.message || "Fehler beim Erstellen der Einladung");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-foreground">Teammitglied einladen</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Senden Sie eine Einladung per E-Mail
        </p>
      </div>

      {/* Seat Limit Warning */}
      {isAtLimit && (
        <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-3">
          <XCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-destructive">Seat-Limit erreicht</p>
            <p className="text-xs text-destructive/80 mt-0.5">
              Alle {maxSeats} Plätze sind belegt. Entfernen Sie inaktive Mitglieder oder upgraden Sie Ihren Plan.
            </p>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-3 h-8 text-xs border-destructive/30 text-destructive hover:bg-destructive/10"
              onClick={() => navigate('/settings/workspace')}
            >
              <ArrowUpRight className="h-3 w-3 mr-1.5" />
              Plan upgraden
            </Button>
          </div>
        </div>
      )}

      {isNearLimit && !isAtLimit && (
        <div className={cn(
          "mb-4 p-3 rounded-lg flex items-start gap-3",
          isLastSeat 
            ? "bg-destructive/10 border border-destructive/20" 
            : "bg-yellow-500/10 border border-yellow-500/20"
        )}>
          <AlertTriangle className={cn(
            "h-5 w-5 flex-shrink-0 mt-0.5",
            isLastSeat ? "text-destructive" : "text-yellow-600 dark:text-yellow-400"
          )} />
          <div>
            <p className={cn(
              "text-sm font-medium",
              isLastSeat ? "text-destructive" : "text-yellow-700 dark:text-yellow-300"
            )}>
              {isLastSeat ? "Letzter Platz verfügbar" : `Nur noch ${remainingSeats} Plätze verfügbar`}
            </p>
            <p className={cn(
              "text-xs mt-0.5",
              isLastSeat ? "text-destructive/80" : "text-yellow-600/80 dark:text-yellow-400/80"
            )}>
              {usedSeats} von {maxSeats} Seats belegt. Nach dieser Einladung {isLastSeat ? "sind alle Plätze vergeben" : `bleiben ${remainingSeats! - 1} übrig`}.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium">E-Mail-Adresse</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="email"
              type="email"
              placeholder="kollege@firma.de"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isAtLimit}
              className="pl-10 h-10"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="role" className="text-sm font-medium">Rolle</Label>
          <div className="relative">
            <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10 pointer-events-none" />
            <Select 
              value={role} 
              onValueChange={(value) => setRole(value as 'admin' | 'member')}
              disabled={isAtLimit}
            >
              <SelectTrigger id="role" className="pl-10 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Mitglied</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">
            Admins können Teammitglieder verwalten und Einstellungen ändern.
          </p>
        </div>

        <Button 
          type="submit" 
          disabled={loading || !email || isAtLimit}
          className="w-full h-10"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Wird gesendet...
            </>
          ) : isAtLimit ? (
            <>
              <XCircle className="h-4 w-4 mr-2" />
              Limit erreicht
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              Einladung senden
            </>
          )}
        </Button>
      </form>
    </div>
  );
}
